// DT1 dev order — the Alt/Cmd+K dev-tools dialog, NON-PRODUCTION ONLY: the v3
// FeaturesModal panel comes back (reverses RB1's kill) and RB1's AI-model
// override folds in as a second tab. One hotkey, one dialog, mounted once at
// the App root (v3 mounted per-shell: sider + ExperienceLayout).
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChooseAIModelPanel } from '@/components/ap/devtools/ChooseAIModelPanel';
import { DevToolsFeaturesPanel } from '@/components/ap/devtools/DevToolsFeaturesPanel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AppConfig } from '@/config/appConfig';
import { useCurrentUser, useEnabledFeatures } from '@/hooks/dataHelpers';
import { isProduction } from '@/lib/appConstants';
import { cn } from '@/lib/cn';
import { selectEmbedded, useCommonStore } from '@/store/commonStore';

// Tab gates. Features: the v3 sider gate minus its dead `&& false` — the
// (flag ∪ non-prod ∪ superadmin/support) disjunction is subsumed by the
// dialog-level non-prod gate, leaving the admin-role requirement. AI model:
// the v3 ExperienceLayout gate, verbatim (RB1).
const useDevToolsTabs = () => {
  const currentUser = useCurrentUser();
  const enabledFeatures = useEnabledFeatures();
  const embedded = useCommonStore(selectEmbedded);

  const currentUserRole = currentUser?.role;
  const isAdminOrTeacher = currentUserRole === 'admin' || currentUserRole === 'teacher';
  const isQBAuthor = currentUserRole === 'qb_author';
  const isChina = AppConfig.region === 'china';
  const notProductionMode = AppConfig.mode !== 'production';

  // Features stays non-production-only; the AI-model tab is LIVE in
  // production for the v3 ExperienceLayout Alt+K audience (qb_author /
  // choose_ai_model orgs) — R15b-b3 re-home of that layout shortcut.
  const featuresTab = currentUserRole === 'admin' && !isProduction;
  const modelTab =
    !embedded &&
    !isChina &&
    (isQBAuthor || ((notProductionMode || enabledFeatures.choose_ai_model) && isAdminOrTeacher));

  return { featuresTab, modelTab };
};

export const DevToolsDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { featuresTab, modelTab } = useDevToolsTabs();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Fixed height when the (long) features list can show — switching to
        // the short ai-model tab collapsed the frame. Model-only stays auto.
        className={cn(
          'sm:max-w-3xl',
          featuresTab ? 'flex h-[85vh] flex-col' : 'max-h-[85vh] overflow-y-auto'
        )}
        data-testid="devtools-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('DevToolsDialog.dev_tools')}</DialogTitle>
          <DialogDescription>{t('DevToolsDialog.non_production_tooling')}</DialogDescription>
        </DialogHeader>

        <Tabs
          defaultValue={featuresTab ? 'features' : 'ai-model'}
          className={cn(featuresTab && 'min-h-0 flex-1')}
        >
          <TabsList>
            {featuresTab && (
              <TabsTrigger value="features" data-testid="devtools-tab-features">
                {t('CommonText.features')}
              </TabsTrigger>
            )}
            {modelTab && (
              <TabsTrigger value="ai-model" data-testid="devtools-tab-ai-model">
                {t('DevToolsDialog.ai_model')}
              </TabsTrigger>
            )}
          </TabsList>
          {featuresTab && (
            <TabsContent value="features" className="min-h-0 overflow-y-auto">
              <DevToolsFeaturesPanel />
            </TabsContent>
          )}
          {modelTab && (
            <TabsContent value="ai-model" className={cn(featuresTab && 'min-h-0 overflow-y-auto')}>
              <ChooseAIModelPanel onClose={() => onOpenChange(false)} />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

/** Global Alt/Cmd+K host — v3 sider combo (k or K). Production mounts ONLY
 *  for the model-tab audience (v3 ExperienceLayout canAccessAIModal gate). */
export const DevToolsHotkey = () => {
  const { featuresTab, modelTab } = useDevToolsTabs();
  const [open, setOpen] = useState(false);

  const available = featuresTab || modelTab;

  const handleKeyboardShortcuts = useCallback((e: KeyboardEvent) => {
    if ((e.altKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
      e.preventDefault();
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    if (available) {
      document.addEventListener('keydown', handleKeyboardShortcuts);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyboardShortcuts);
    };
  }, [available, handleKeyboardShortcuts]);

  if (!available || !open) return null;
  return <DevToolsDialog open={open} onOpenChange={setOpen} />;
};
